import { type ReactNode } from 'react';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { PageTitle, PageDescription } from './Typography';
import { cn } from '../../lib/utils';
import { hideTitleUnderMobileChrome, pageBodyUnderMobileChrome } from './PageLayout';

interface AdminSubPageLayoutProps {
  title: string;
  description?: string;
  /** Optional leading icon rendered before the title text. */
  icon?: ReactNode;
  /** Optional right-side action(s), e.g. a refresh button. */
  actions?: ReactNode;
  /**
   * Optional docs slug forwarded to `PageTitle.docs`. Supports the
   * `slug#anchor` form so admin sub-pages can deep-link into a specific
   * section of the docs.
   */
  docs?: string;
  children: ReactNode;
  /**
   * Optional override for `document.title`. Defaults to `title`. Pass
   * `null` to skip setting the title.
   */
  documentTitle?: string | null;
}

/**
 * Shared layout for admin sub-pages.
 * Provides consistent padding, max-width constraint, and a title/description
 * header using the canonical Typography primitives.
 */
export function AdminSubPageLayout({
  title,
  description,
  icon,
  actions,
  docs,
  children,
  documentTitle,
}: AdminSubPageLayoutProps) {
  useDocumentTitle(documentTitle === null ? null : (documentTitle ?? title));
  return (
    <div
      data-testid="admin-sub-page-layout"
      className={cn('flex-1 overflow-y-auto px-4 pb-4 pt-3 md:p-6', pageBodyUnderMobileChrome)}
    >
      <div className="mx-auto max-w-4xl space-y-6">
        <div
          className={cn(
            'flex items-start justify-between gap-3 sm:gap-4',
            !actions && hideTitleUnderMobileChrome,
          )}
        >
          <div className="min-w-0">
            <PageTitle icon={icon} docs={docs} className={hideTitleUnderMobileChrome}>
              {title}
            </PageTitle>
            {description ? <PageDescription>{description}</PageDescription> : null}
          </div>
          {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
        </div>
        {children}
      </div>
    </div>
  );
}
